// ----------------------------------------------------------------------
// Carbon footprint / environmental-impact calculations (single source of truth).
//
// This module centralises the CO2 / trees / avoided-emissions maths that used to
// be copy-pasted across EnvironmentalImpact.js and EnergyConsumptionTrends.js.
// Keep all emission constants and formulas here so the dashboard stays internally
// consistent and the figures are unit-testable.
//
// IMPORTANT — the constants below are approximations. Validate/localise before
// presenting the numbers as authoritative:
//   * GRID_EMISSION_FACTOR (0.5 kg CO2/kWh) is a conservative *generic* value and
//     is on the high side for 2024-2026. Real grid intensity is strongly region-
//     dependent: global avg ~0.44, India ~0.71, US ~0.38, EU ~0.21, UK ~0.12.
//     Source: IEA Electricity 2025; EEA; Ember; Carbon Brief (2024 data).
//     If this product is India-focused, ~0.71 is more accurate. Prefer making
//     this region-configurable rather than hard-coding a single global default.
//   * RENEWABLE_EMISSION_FACTOR (0.05 kg CO2/kWh) is a lifecycle figure. It matches
//     the IPCC AR5 solar-PV median (~48 g) and is conservative (too high for wind
//     ~11 g), so it slightly *understates* the renewable benefit. Source: IPCC AR5
//     Annex III.
//   * TREE_CO2_PER_YEAR (22 kg/tree/yr) is a widely cited mid-range rule of thumb;
//     the real range is ~10-40 kg/yr depending on species/age/climate. Treat as an
//     approximation. Source: Arbor Day / One Tree Planted / EcoTree.
//   * "carbonOffset" here is really *avoided emissions* (baseline grid-only CO2
//     minus actual CO2 given the renewable mix) — a valid illustrative estimate,
//     but NOT a tradable "offset" and it must not be netted into a formal GHG
//     Scope 2 inventory. Source: GHG Protocol Scope 2 Guidance.
// ----------------------------------------------------------------------

export const CARBON_CONSTANTS = {
  // kg CO2 emitted per kWh of grid electricity.
  GRID_EMISSION_FACTOR: 0.5,
  // kg CO2 emitted per kWh of renewable electricity (lifecycle).
  RENEWABLE_EMISSION_FACTOR: 0.05,
  // Assumed share of energy sourced from renewables (0..1).
  RENEWABLE_PERCENTAGE: 0.2,
  // kg CO2 absorbed by one tree per year.
  TREE_CO2_PER_YEAR: 22,
};

const toNumberOrUndefined = (value) => {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// Build a constants object from the built-in defaults, deployment-level env vars,
// and explicit (e.g. per-team) overrides — in increasing order of precedence.
// Mirrors the backend util (cms-api/src/util/carbonFootprint.js) so the API and
// dashboard agree. Env vars are CRA build-time REACT_APP_CARBON_* values;
// RENEWABLE_PERCENTAGE is a fraction 0..1 in both env and overrides.
export const buildCarbonConstants = (overrides = {}, env = process.env) => {
  const envOverrides = env
    ? {
        GRID_EMISSION_FACTOR: toNumberOrUndefined(env.REACT_APP_CARBON_GRID_EMISSION_FACTOR),
        RENEWABLE_EMISSION_FACTOR: toNumberOrUndefined(
          env.REACT_APP_CARBON_RENEWABLE_EMISSION_FACTOR
        ),
        RENEWABLE_PERCENTAGE: toNumberOrUndefined(env.REACT_APP_CARBON_RENEWABLE_PERCENTAGE),
        TREE_CO2_PER_YEAR: toNumberOrUndefined(env.REACT_APP_CARBON_TREE_CO2_PER_YEAR),
      }
    : {};

  const merged = { ...CARBON_CONSTANTS };
  [envOverrides, overrides].forEach((source) => {
    if (!source) return;
    Object.keys(CARBON_CONSTANTS).forEach((key) => {
      const n = toNumberOrUndefined(source[key]);
      if (n !== undefined) merged[key] = n;
    });
  });

  merged.GRID_EMISSION_FACTOR = Math.max(0, merged.GRID_EMISSION_FACTOR);
  merged.RENEWABLE_EMISSION_FACTOR = Math.max(0, merged.RENEWABLE_EMISSION_FACTOR);
  merged.RENEWABLE_PERCENTAGE = clamp(merged.RENEWABLE_PERCENTAGE, 0, 1);
  merged.TREE_CO2_PER_YEAR =
    merged.TREE_CO2_PER_YEAR > 0 ? merged.TREE_CO2_PER_YEAR : CARBON_CONSTANTS.TREE_CO2_PER_YEAR;

  return merged;
};

const roundTo = (value, decimals = 0) => {
  const factor = 10 ** decimals;
  return Math.round((Number(value) || 0) * factor) / factor;
};

// A transaction is "completed" if any of the possible stop markers is present.
export const isTransactionCompleted = (transaction) =>
  Boolean(
    transaction &&
      (transaction.stop_time ||
        transaction.stop_event_timestamp ||
        transaction.stoptime ||
        transaction.stop_timestamp ||
        transaction.stop_value)
  );

export const getTransactionEnergyKwh = (transaction) =>
  parseFloat(transaction && transaction.energy_consumed) || 0;

// Parse the loosely-formatted timestamps the API returns ("YYYY-MM-DD HH:mm:ss"
// with optional fractional seconds, sometimes without a trailing Z).
const parseApiDate = (rawTime) => {
  if (!rawTime) return null;
  const normalised =
    rawTime.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1') +
    (rawTime.includes('Z') ? '' : 'Z');
  return new Date(normalised);
};

// Core formula: given energy in kWh, return the raw (unrounded) CO2 figures.
export const carbonForEnergy = (energyKwh, constants = CARBON_CONSTANTS) => {
  const {
    GRID_EMISSION_FACTOR,
    RENEWABLE_EMISSION_FACTOR,
    RENEWABLE_PERCENTAGE,
  } = constants;

  const energy = Number(energyKwh) || 0;
  // Baseline: all energy drawn from the grid.
  const baselineCO2 = energy * GRID_EMISSION_FACTOR;
  // Actual: assumed grid/renewable mix.
  const gridEnergy = energy * (1 - RENEWABLE_PERCENTAGE);
  const renewableEnergy = energy * RENEWABLE_PERCENTAGE;
  const actualCO2 =
    gridEnergy * GRID_EMISSION_FACTOR + renewableEnergy * RENEWABLE_EMISSION_FACTOR;
  // Avoided emissions relative to the grid-only baseline (labelled "offset" in UI).
  const avoidedCO2 = baselineCO2 - actualCO2;

  return { energy, baselineCO2, actualCO2, avoidedCO2 };
};

export const treesFromCO2 = (co2Kg, constants = CARBON_CONSTANTS) =>
  (Number(co2Kg) || 0) / constants.TREE_CO2_PER_YEAR;

// Sum energy across completed transactions with positive energy.
export const sumCompletedEnergy = (transactions = []) =>
  (Array.isArray(transactions) ? transactions : []).reduce((total, transaction) => {
    const energy = getTransactionEnergyKwh(transaction);
    return isTransactionCompleted(transaction) && energy > 0 ? total + energy : total;
  }, 0);

// Convert a scenario-derived percentage reduction into a 1..10 score.
const scoreFromReduction = (carbonReductionPct) =>
  Math.min(10, Math.max(1, 5 + carbonReductionPct / 10));

// ----------------------------------------------------------------------
// Aggregate metrics from a list of raw transactions.
// NOTE: trees are derived from the summed CO2 and rounded ONCE at the end.
// (The old per-transaction Math.round(co2/22) rounded every session to the
// nearest whole tree before summing, so many small sessions rounded to 0 and
// the total was understated. This version fixes that.)
// ----------------------------------------------------------------------
export const calculateEnvironmentalMetrics = (
  transactions,
  constants = CARBON_CONSTANTS
) => {
  const empty = {
    totalCO2Emissions: 0,
    baselineCO2Emissions: 0,
    carbonFootprintReduction: 0,
    sustainabilityScore: 0,
    treesEquivalent: 0,
    carbonOffset: 0,
    renewablePercentage: 0,
    totalEnergy: 0,
    avgDailyCO2: 0,
    avgDailyTrees: 0,
    avgDailyOffset: 0,
  };

  if (!transactions || !Array.isArray(transactions) || transactions.length === 0) {
    return empty;
  }

  let totalEnergy = 0;
  let totalCO2 = 0;
  let totalOffset = 0;

  transactions.forEach((transaction) => {
    const energy = getTransactionEnergyKwh(transaction);
    if (isTransactionCompleted(transaction) && energy > 0) {
      const { actualCO2, avoidedCO2 } = carbonForEnergy(energy, constants);
      totalEnergy += energy;
      totalCO2 += actualCO2;
      totalOffset += avoidedCO2;
    }
  });

  const baselineCO2 = totalEnergy * constants.GRID_EMISSION_FACTOR;
  const carbonReduction =
    baselineCO2 > 0 ? ((baselineCO2 - totalCO2) / baselineCO2) * 100 : 0;
  const sustainabilityScore = scoreFromReduction(carbonReduction);
  const totalTrees = treesFromCO2(totalCO2, constants);

  // Daily averages span from the first transaction to now.
  const firstTxnTime =
    transactions[0]?.start_time ||
    transactions[0]?.created_at ||
    new Date().toISOString();
  const firstTxnDate = parseApiDate(firstTxnTime) || new Date();
  const days = Math.max(
    1,
    Math.ceil((new Date() - firstTxnDate) / (1000 * 60 * 60 * 24)) + 1
  );

  return {
    totalCO2Emissions: roundTo(totalCO2),
    baselineCO2Emissions: roundTo(baselineCO2),
    carbonFootprintReduction: roundTo(carbonReduction, 1),
    sustainabilityScore: roundTo(sustainabilityScore, 1),
    treesEquivalent: roundTo(totalTrees),
    carbonOffset: roundTo(totalOffset),
    renewablePercentage: roundTo(constants.RENEWABLE_PERCENTAGE * 100),
    totalEnergy: roundTo(totalEnergy, 1),
    avgDailyCO2: roundTo(totalCO2 / days),
    avgDailyTrees: roundTo(totalTrees / days),
    avgDailyOffset: roundTo(totalOffset / days),
  };
};

// ----------------------------------------------------------------------
// Daily time series (CO2 / trees / offset) for the environmental-impact chart.
// Returns { data: [ {name,type,data,color} ], labels: [] } for ApexCharts.
// ----------------------------------------------------------------------
const emptySeries = () => ({
  data: [
    { name: 'CO₂ Emissions (kg)', type: 'line', data: [0], color: '#E53E3E' },
    { name: 'Trees Saved', type: 'line', data: [0], color: '#38A169' },
    { name: 'Carbon Offset (kg)', type: 'line', data: [0], color: '#3182CE' },
  ],
  labels: ['No Data'],
});

export const buildEnvironmentalTimeSeries = (
  transactions,
  timeInterval,
  constants = CARBON_CONSTANTS
) => {
  if (!transactions || !Array.isArray(transactions) || !timeInterval) {
    return emptySeries();
  }

  const { start, end } = timeInterval;
  const startDate = new Date(start);
  const endDate = new Date(end);
  const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

  const co2Emissions = new Array(days).fill(0);
  const treesSaved = new Array(days).fill(0);
  const carbonOffset = new Array(days).fill(0);
  const labels = [];
  const labelDates = [];

  for (let i = 0; i < days; i += 1) {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + i);
    labelDates.push(new Date(currentDate));
    labels.push(
      currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    );

    let dayCO2 = 0;
    let dayOffset = 0;

    transactions.forEach((transaction) => {
      const transactionDate = parseApiDate(
        transaction.start_time || transaction.created_at
      );
      if (!transactionDate) return;
      if (transactionDate.toDateString() !== currentDate.toDateString()) return;

      const energy = getTransactionEnergyKwh(transaction);
      if (isTransactionCompleted(transaction) && energy > 0) {
        const { actualCO2, avoidedCO2 } = carbonForEnergy(energy, constants);
        dayCO2 += actualCO2;
        dayOffset += avoidedCO2;
      }
    });

    co2Emissions[i] = Math.round(dayCO2);
    // Round trees once per day from the day's total CO2 (not per transaction).
    treesSaved[i] = Math.round(treesFromCO2(dayCO2, constants));
    carbonOffset[i] = Math.round(dayOffset);
  }

  // Drop a trailing future date (e.g. an inclusive end that lands past today).
  let filteredLabels = labels;
  let filteredCo2 = co2Emissions;
  let filteredTrees = treesSaved;
  let filteredOffset = carbonOffset;

  if (labelDates.length > 0) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const lastDate = new Date(labelDates[labelDates.length - 1]);
    lastDate.setHours(0, 0, 0, 0);
    if (lastDate > today) {
      filteredLabels = labels.slice(0, -1);
      filteredCo2 = co2Emissions.slice(0, -1);
      filteredTrees = treesSaved.slice(0, -1);
      filteredOffset = carbonOffset.slice(0, -1);
    }
  }

  return {
    data: [
      { name: 'CO₂ Emissions (kg)', type: 'line', data: filteredCo2, color: '#E53E3E' },
      { name: 'Trees Saved', type: 'line', data: filteredTrees, color: '#38A169' },
      { name: 'Carbon Offset (kg)', type: 'line', data: filteredOffset, color: '#3182CE' },
    ],
    labels: filteredLabels,
  };
};

// ----------------------------------------------------------------------
// Environmental impact derived from an already-summed total energy (kWh).
// Used where only an aggregate is available (Energy Consumption Trends card).
// Shape matches the backend `environmentalImpact` object so callers can prefer
// the server figure and fall back to this without branching on field names.
// ----------------------------------------------------------------------
export const environmentalImpactFromEnergy = (
  totalEnergy,
  constants = CARBON_CONSTANTS
) => {
  const { baselineCO2, actualCO2, avoidedCO2 } = carbonForEnergy(totalEnergy, constants);
  const carbonReduction =
    baselineCO2 > 0 ? ((baselineCO2 - actualCO2) / baselineCO2) * 100 : 0;

  return {
    totalCO2Emissions: roundTo(actualCO2),
    baselineCO2Emissions: roundTo(baselineCO2),
    carbonFootprintReduction: roundTo(carbonReduction, 1),
    sustainabilityScore: roundTo(scoreFromReduction(carbonReduction), 1),
    treesEquivalent: roundTo(treesFromCO2(actualCO2, constants)),
    carbonOffset: roundTo(avoidedCO2),
    renewablePercentage: roundTo(constants.RENEWABLE_PERCENTAGE * 100),
  };
};
